// File: src/pages/DocumentsPage.js

// --- 1. ИМПОРТЫ ---
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast'; // Для уведомлений
import apiService from '../services/apiService'; // Сервис для работы с API
import LoadingSpinner from '../components/LoadingSpinner'; // Индикатор загрузки
import './DocumentsPage.css'; // Подключаем стили для страницы

// --- 2. КОМПОНЕНТ СТРАНИЦЫ ---
const DocumentsPage = () => {
    // --- 3. СОСТОЯНИЕ КОМПОНЕНТА (STATE) ---
    const [documents, setDocuments] = useState([]); // Хранит список документов
    const [selectedFile, setSelectedFile] = useState(null); // Хранит выбранный для загрузки файл
    const [isLoading, setIsLoading] = useState(true); // Отвечает за начальную загрузку списка
    const [isUploading, setIsUploading] = useState(false); // Отвечает за процесс загрузки файла
    const fileInputRef = useRef(null); // Ссылка на input для его очистки после загрузки

    // --- 4. ЗАГРУЗКА ДАННЫХ (EFFECT) ---
    // Загружаем список документов при первом рендере страницы
    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                const data = await apiService.getDocuments();
                setDocuments(data || []); // Если данных нет, устанавливаем пустой массив
            } catch (error) {
                toast.error(`Failed to fetch documents: ${error.message}`);
            } finally {
                setIsLoading(false); // Убираем индикатор загрузки в любом случае
            }
        };
        fetchDocuments();
    }, []); // Пустой массив зависимостей гарантирует, что эффект выполнится один раз

    // --- 5. ОБРАБОТЧИКИ СОБЫТИЙ ---
    // Обработчик выбора файла в input
    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    // Обработчик загрузки файла на сервер
    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error('Please select a file to upload.');
            return;
        }

        setIsUploading(true);
        const uploadToast = toast.loading('Uploading document...');
        try {
            const newDocument = await apiService.uploadDocument(selectedFile);
            // Добавляем новый документ в начало списка для немедленного отображения (оптимистичное обновление)
            setDocuments(prevDocs => [newDocument, ...prevDocs]);
            toast.success('Document uploaded successfully!', { id: uploadToast });
            
            // Очищаем поле выбора файла
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            toast.error(`Upload failed: ${error.message}`, { id: uploadToast });
        } finally {
            setIsUploading(false);
        }
    };

    // --- 6. УСЛОВНЫЙ РЕНДЕРИНГ ---
    // Если идет начальная загрузка, показываем спиннер
    if (isLoading) {
        return <LoadingSpinner message="Loading documents..." />;
    }

    // --- 7. JSX-РАЗМЕТКА ---
    return (
        <div className="container">
            <h1 className="documents-header">Document Management</h1>

            {/* Секция для загрузки нового документа */}
            <div className="card upload-section">
                <div className="form-group">
                    <label htmlFor="file-upload" className="form-label">Upload New Document</label>
                    <input
                        id="file-upload"
                        type="file"
                        className="form-input"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        disabled={isUploading}
                    />
                </div>
                <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={isUploading || !selectedFile} // Кнопка неактивна и во время загрузки, и если файл не выбран
                >
                    {isUploading ? 'Uploading...' : 'Upload'}
                </button>
            </div>

            {/* Список загруженных документов */}
            <div className="document-list">
                {documents.length > 0 ? (
                    documents.map(doc => (
                        <div key={doc.id} className="card document-item">
                            <div className="doc-item-header">
                                <h2 className="doc-filename">{doc.filename}</h2>
                                <span className={`badge badge-${doc.status?.toLowerCase() || 'warning'}`}>
                                    {doc.status || 'Processing'}
                                </span>
                            </div>
                            <p className="doc-summary">{doc.summary || 'Summary is being generated...'}</p>
                            <span className="doc-timestamp">
                                Uploaded on: {new Date(doc.created_at).toLocaleString()}
                            </span>
                        </div>
                    ))
                ) : (
                    <div className="card no-documents-card">
                        <p>No documents found. Upload your first document to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- 8. ЭКСПОРТ ---
export default DocumentsPage;
